<script setup>
import { $api } from "@/utils/api";

definePage({
  meta: {
    requiresAuth: true,
  },
});

const route = useRoute();
const clienteId = computed(() => Number(route.params.id));

const clienteNome = ref("");
const projetos = ref([]);
const podeExcluirRdo = ref(false);
const loading = ref(true);
const erro = ref("");

// Filtros
const filtroStatus = ref("");
const filtroNumero = ref("");
const filtroNome = ref("");
const filtroDataInicial = ref("");

async function fetchProjetos() {
  if (!clienteId.value) return;
  loading.value = true;
  erro.value = "";
  try {
    const params = new URLSearchParams();
    if (filtroStatus.value !== "" && filtroStatus.value != null) params.set("status", filtroStatus.value);
    if (filtroNumero.value?.trim()) params.set("numero_projeto", filtroNumero.value.trim());
    if (filtroNome.value?.trim()) params.set("nome_projeto", filtroNome.value.trim());
    if (filtroDataInicial.value) params.set("data_inicial", filtroDataInicial.value);
    const data = await $api(`/rdo/clientes/${clienteId.value}/projetos?${params}`);
    clienteNome.value = data?.cliente_nome ?? "";
    projetos.value = data?.projetos ?? [];
    podeExcluirRdo.value = data?.pode_excluir_rdo ?? false;
  } catch (e) {
    console.error(e);
    erro.value = e?.data?.error || e?.message || "Erro ao carregar projetos.";
    projetos.value = [];
  } finally {
    loading.value = false;
  }
}

function formatarData(data) {
  if (!data) return "—";
  return new Date(data).toLocaleDateString("pt-BR");
}

watch(clienteId, () => {
  fetchProjetos();
}, { immediate: true });

function limparFiltros() {
  filtroStatus.value = "";
  filtroNumero.value = "";
  filtroNome.value = "";
  filtroDataInicial.value = "";
  fetchProjetos();
}

const excluindoId = ref(null);
const dialogConfirmarExcluir = ref(false);
const dialogAviso = ref(false);
const avisoMsg = ref("");
const projetoParaExcluir = ref(null);

function abrirConfirmarExcluir(projeto) {
  projetoParaExcluir.value = projeto;
  dialogConfirmarExcluir.value = true;
}

function fecharConfirmarExcluir() {
  if (!excluindoId.value) {
    dialogConfirmarExcluir.value = false;
    projetoParaExcluir.value = null;
  }
}

async function confirmarExcluirRdo() {
  const projeto = projetoParaExcluir.value;
  if (!projeto) return;
  excluindoId.value = projeto.id;
  try {
    await $api(`/rdo/${projeto.id}`, { method: "DELETE" });
    dialogConfirmarExcluir.value = false;
    projetoParaExcluir.value = null;
    await fetchProjetos();
  } catch (e) {
    console.error(e);
    avisoMsg.value = e?.data?.error || e?.message || "Erro ao excluir RDO.";
    dialogAviso.value = true;
  } finally {
    excluindoId.value = null;
  }
}

function fecharAviso() {
  dialogAviso.value = false;
  avisoMsg.value = "";
}
</script>

<template>
  <div>
    <VRow>
      <VCol cols="12">
        <VCard>
          <VCardTitle class="d-flex align-center pa-4">
            <VBtn
              icon
              variant="text"
              size="small"
              :to="{ name: 'rdo' }"
              class="me-2"
            >
              <VIcon icon="tabler-arrow-left" />
            </VBtn>
            <span class="text-h5">Detalhamento do Cliente</span>
            <VSpacer />
            <span v-if="clienteNome" class="text-body-1 text-medium-emphasis">
              {{ clienteNome }}
            </span>
          </VCardTitle>
          <VDivider />
          <VCardText class="pa-4">
            <!-- Filtros -->
            <div class="d-flex flex-wrap align-end gap-3 mb-4">
              <VTextField
                v-model="filtroNumero"
                label="Número do Projeto"
                placeholder="Ex.: 2024/001"
                density="compact"
                hide-details
                clearable
                class="flex-grow-1 flex-md-grow-0"
                style="min-width: 140px; max-width: 180px;"
                variant="outlined"
              />
              <VTextField
                v-model="filtroNome"
                label="Nome do Projeto"
                placeholder="Buscar por nome"
                density="compact"
                hide-details
                clearable
                class="flex-grow-1 flex-md-grow-0"
                style="min-width: 180px; max-width: 260px;"
                variant="outlined"
              />
              <VTextField
                v-model="filtroDataInicial"
                label="Data inicial"
                type="date"
                density="compact"
                hide-details
                clearable
                class="flex-grow-1 flex-md-grow-0"
                style="min-width: 150px; max-width: 160px;"
                variant="outlined"
              />
              <VTextField
                v-model="filtroStatus"
                label="Status do Projeto"
                placeholder="Código do status"
                type="number"
                density="compact"
                hide-details
                clearable
                class="flex-grow-1 flex-md-grow-0"
                style="min-width: 120px; max-width: 140px;"
                variant="outlined"
              />
              <VBtn color="primary" @click="fetchProjetos">
                <VIcon icon="tabler-search" start />
                Filtrar
              </VBtn>
              <VBtn variant="tonal" color="secondary" @click="limparFiltros">
                Limpar filtros
              </VBtn>
            </div>

            <VProgressLinear v-if="loading" indeterminate class="mb-4" />
            <VAlert v-if="erro" type="error" class="mb-4">{{ erro }}</VAlert>

            <VTable v-if="!loading && !erro && projetos.length > 0" density="comfortable">
              <thead>
                <tr>
                  <th>Nº Projeto</th>
                  <th>Nome do Projeto</th>
                  <th>Status</th>
                  <th>Data Inicial</th>
                  <th>Data Final</th>
                  <th width="100">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="projeto in projetos" :key="projeto.id">
                  <td>{{ projeto.numero_projeto ?? '—' }}</td>
                  <td>{{ projeto.nome ?? '—' }}</td>
                  <td>
                    <VChip v-if="projeto.cod_status != null" size="small" variant="tonal">
                      {{ projeto.cod_status }}
                    </VChip>
                    <span v-else>—</span>
                  </td>
                  <td>{{ formatarData(projeto.data_inicial) }}</td>
                  <td>{{ formatarData(projeto.data_final) }}</td>
                  <td>
                    <VTooltip text="Abrir RDO" location="top">
                      <template #activator="{ props: tProps }">
                        <VBtn
                          v-bind="tProps"
                          icon
                          variant="text"
                          size="small"
                          color="primary"
                          :to="{ name: 'rdo-id', params: { id: projeto.id } }"
                        >
                          <VIcon icon="tabler-search" />
                        </VBtn>
                      </template>
                    </VTooltip>
                    <VTooltip
                      v-if="podeExcluirRdo"
                      text="Excluir RDO"
                      location="top"
                    >
                      <template #activator="{ props: tProps }">
                        <VBtn
                          v-bind="tProps"
                          icon
                          variant="text"
                          size="small"
                          color="error"
                          :loading="excluindoId === projeto.id"
                          :disabled="excluindoId !== null"
                          @click="abrirConfirmarExcluir(projeto)"
                        >
                          <VIcon icon="tabler-trash" />
                        </VBtn>
                      </template>
                    </VTooltip>
                  </td>
                </tr>
              </tbody>
            </VTable>

            <VAlert v-else-if="!loading && !erro" type="info" variant="tonal">
              Nenhum projeto encontrado para este cliente.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Modal confirmar exclusão -->
    <VDialog v-model="dialogConfirmarExcluir" max-width="400" persistent>
      <VCard>
        <VCardTitle class="text-h6">Excluir RDO</VCardTitle>
        <VCardText>
          Deseja realmente excluir o RDO "{{ projetoParaExcluir?.nome ?? projetoParaExcluir?.numero_projeto ?? '' }}"?
          Esta ação não pode ser desfeita.
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" :disabled="!!excluindoId" @click="fecharConfirmarExcluir">
            Cancelar
          </VBtn>
          <VBtn color="error" :loading="!!excluindoId" @click="confirmarExcluirRdo">
            Excluir
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Modal aviso/erro -->
    <VDialog v-model="dialogAviso" max-width="400" persistent>
      <VCard>
        <VCardTitle class="d-flex align-center">
          <VIcon icon="tabler-alert-circle" color="error" class="me-2" />
          Aviso
          <VSpacer />
          <VBtn icon variant="text" size="small" @click="fecharAviso">
            <VIcon icon="tabler-x" />
          </VBtn>
        </VCardTitle>
        <VCardText>{{ avisoMsg }}</VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn color="primary" variant="tonal" @click="fecharAviso">
            OK
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
